import type { User, ObservadorData } from '../../../models';
import { ICON_MAP } from './iconMap';
import type { ComponentType } from 'react';

// Funções de formatação de datas
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

export const formatDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

// Funções de status
export const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'danger' | 'info' | 'default' => {
  switch (status?.toUpperCase()) {
    case 'EMATRIBUICAO':
      return 'warning';
    case 'ATRIBUIDA':
      return 'info';
    case 'EMPROGRESSO':
      return 'info';
    case 'AGUARDANDOAUTORIZACAO':
      return 'warning';
    case 'SOLUCIONADA':
      return 'success';
    case 'FINALIZADA':
      return 'success';
    case 'DEVOLVIDA':
      return 'warning';
    case 'ERRADA':
      return 'error';
    case 'EXCLUIDA':
      return 'danger';
    case 'CANCELADA':
      return 'error';
    case 'REPROVADA':
      return 'danger';
    default:
      return 'default';
  }
};

export const getStatusIcon = (status: string): ComponentType<{ className?: string }> => {
  switch (status?.toUpperCase()) {
    case 'EMATRIBUICAO':
      return ICON_MAP.HiUserPlus;
    case 'ATRIBUIDA':
      return ICON_MAP.HiUserCircle;
    case 'EMPROGRESSO':
      return ICON_MAP.HiPlay;
    case 'AGUARDANDOAUTORIZACAO':
      return ICON_MAP.HiClock;
    case 'SOLUCIONADA':
      return ICON_MAP.HiCheckCircle;
    case 'FINALIZADA':
      return ICON_MAP.HiCheckBadge;
    case 'DEVOLVIDA':
      return ICON_MAP.HiArrowUturnLeft;
    case 'ERRADA':
      return ICON_MAP.HiXCircle;
    case 'EXCLUIDA':
      return ICON_MAP.HiTrash;
    case 'CANCELADA':
      return ICON_MAP.HiNoSymbol;
    case 'REPROVADA':
      return ICON_MAP.HiXMark;
    default:
      return ICON_MAP.HiDocumentText;
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'EMATRIBUICAO':
      return 'Em Atribuição';
    case 'ATRIBUIDA':
      return 'Atribuída';
    case 'EMPROGRESSO':
      return 'Em Progresso';
    case 'AGUARDANDOAUTORIZACAO':
      return 'Aguardando Autorização';
    case 'SOLUCIONADA':
      return 'Solucionada';
    case 'FINALIZADA':
      return 'Finalizada';
    case 'DEVOLVIDA':
      return 'Devolvida';
    case 'ERRADA':
      return 'Errada';
    case 'EXCLUIDA':
      return 'Excluída';
    case 'CANCELADA':
      return 'Cancelada';
    case 'REPROVADA':
      return 'Reprovada';
    default:
      return status || 'Desconhecido';
  }
};

// Funções de prioridade
export const getPriorityColor = (prioridade: string): 'success' | 'warning' | 'error' | 'danger' | 'info' | 'default' => {
  switch (prioridade?.toUpperCase()) {
    case 'BAIXA':
      return 'success';
    case 'NORMAL':
    case 'MEDIA':
      return 'info';
    case 'ALTA':
      return 'warning';
    case 'URGENTE':
      return 'error';
    default:
      return 'default';
  }
};

export const getPriorityIcon = (prioridade: string): ComponentType<{ className?: string }> => {
  switch (prioridade?.toUpperCase()) {
    case 'BAIXA':
      return ICON_MAP.HiFlag;
    case 'NORMAL':
      return ICON_MAP.HiFlag;
    case 'ALTA':
      return ICON_MAP.HiExclamationTriangle;
    case 'URGENTE':
      return ICON_MAP.HiExclamationCircle;
    default:
      return ICON_MAP.HiFlag;
  }
};

// Funções de status de atribuição
export const getAssignmentStatusColor = (status: string): 'success' | 'warning' | 'error' | 'danger' | 'info' | 'default' => {
  switch (status?.toUpperCase()) {
    case 'RECEBIDA':
    case 'PENDENTE':
      return 'warning';
    case 'EMPROGRESSO':
    case 'EM_ATENDIMENTO':
    case 'ACEITA':
      return 'info';
    case 'AGUARDANDOAUTORIZACAO':
      return 'warning';
    case 'SOLUCIONADA':
      return 'success';
    case 'RECUSADA':
    case 'REPROVADA':
    case 'ERRADA':
    case 'CANCELADA':
      return 'error';
    case 'PAUSADA':
      return 'default';
    case 'DEVOLVIDA':
      return 'warning';
    case 'EXCLUIDA':
      return 'danger';
    default:
      return 'default';
  }
};

export const getAssignmentStatusIcon = (status: string): ComponentType<{ className?: string }> => {
  switch (status?.toUpperCase()) {
    case 'RECEBIDA':
    case 'PENDENTE':
      return ICON_MAP.HiClock;
    case 'EMPROGRESSO':
    case 'EM_ATENDIMENTO':
      return ICON_MAP.HiPlay;
    case 'AGUARDANDOAUTORIZACAO':
      return ICON_MAP.HiClock;
    case 'SOLUCIONADA':
      return ICON_MAP.HiCheckBadge;
    case 'RECUSADA':
    case 'REPROVADA':
      return ICON_MAP.HiXCircle;
    case 'ACEITA':
      return ICON_MAP.HiCheckCircle;
    case 'PAUSADA':
      return ICON_MAP.HiPause;
    case 'DEVOLVIDA':
      return ICON_MAP.HiArrowUturnLeft;
    case 'ERRADA':
      return ICON_MAP.HiXMark;
    case 'CANCELADA':
      return ICON_MAP.HiNoSymbol;
    case 'EXCLUIDA':
      return ICON_MAP.HiTrash;
    default:
      return ICON_MAP.HiClock;
  }
};

export const getAssignmentStatusLabel = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'RECEBIDA':
    case 'PENDENTE':
      return 'Pendente';
    case 'EMPROGRESSO':
      return 'Em Progresso';
    case 'AGUARDANDOAUTORIZACAO':
      return 'Aguardando Autorização';
    case 'SOLUCIONADA':
      return 'Solucionada';
    case 'RECUSADA':
      return 'Recusada';
    case 'ACEITA':
      return 'Aceita';
    case 'EM_ATENDIMENTO':
      return 'Em Atendimento';
    case 'PAUSADA':
      return 'Pausada';
    case 'DEVOLVIDA':
      return 'Devolvida';
    case 'ERRADA':
      return 'Errada';
    case 'CANCELADA':
      return 'Cancelada';
    case 'REPROVADA':
      return 'Reprovada';
    case 'EXCLUIDA':
      return 'Excluída';
    default:
      return status || 'Desconhecido';
  }
};

// Função para obter status de atribuição do usuário
export const getUserAssignmentStatus = (demanda: any, userId: number): string => {
  // Primeiro tenta buscar nas atribuições ativas (formato correto)
  // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
  if (demanda?.atribuidas && Array.isArray(demanda.atribuidas)) {
    const userAssignment = demanda.atribuidas.find((attr: any) => 
      attr.usuario_fk === userId && Boolean(attr.active)
    );
    if (userAssignment) {
      return userAssignment.status;
    }
  }

  // Fallback: tenta no formato antigo de demandados
  if (demanda?.demandados && Array.isArray(demanda.demandados)) {
    const userAssignment = demanda.demandados.find((d: any) => d.user_id === userId);
    if (userAssignment) {
      return userAssignment.status;
    }
  }

  return 'PENDENTE';
};

// Função utilitária para normalizar dados de observadores
export const normalizeObservadorData = (observadorData: ObservadorData): { user: User; observador_fk: number } => {
  // Se já é um objeto Observador com propriedade observador
  if ('observador' in observadorData && observadorData.observador) {
    return {
      user: observadorData.observador,
      observador_fk: observadorData.observador_fk
    };
  }
  
  // Se é um User com pivot (formato do backend atual)
  if ('name' in observadorData && 'email' in observadorData) {
    const user = observadorData as User & { pivot?: { demanda_fk: number; observador_fk: number } };
    return {
      user: user,
      observador_fk: user.pivot?.observador_fk || user.id
    };
  }
  
  // Fallback
  throw new Error('Formato de observador inválido');
};